package top.wcpe.allincore.dsl.ir.model

import kotlin.jvm.JvmField

/**
 * 界面级默认字体的 IR 纯数据载体。
 *
 * 对应 DSL 顶层 `font { name=...; bold=...; italic=...; shadow=... }` 块。
 * 四个字段均为三态（`null`=未声明/继承上层）。运行时由 IrInterpreter 转成
 * ui-runtime 的 `FontStyle` 写入 `RendererRegistry.pageFont`，
 * 经 `FontAccess` 页面层作用于本界面所有文字。
 *
 * 之所以在 ir 层另立此类（而非直接复用 ui-runtime 的 `FontStyle`），
 * 是为了避免 ui-dsl-ir 反向依赖 ui-runtime。
 */
class UiScreenFont(
    // val 属性生成 getName()/getShadow()/getBold()/getItalic()，与原 Java getter 名一致。
    val name: String?,
    val shadow: Boolean?,
    val bold: Boolean?,
    val italic: Boolean?,
) {

    /** 是否所有字段为空（无意义的界面字体声明，序列化时按"未声明"处理）。 */
    // 用 is 前缀 val：Java 侧仍见 isEmpty()（原方法名），Kotlin 侧以属性 .isEmpty 访问（保留 runtime 调用点写法）。
    val isEmpty: Boolean
        get() = name == null && shadow == null && bold == null && italic == null

    override fun equals(other: Any?): Boolean {
        if (this === other) {
            return true
        }
        if (other !is UiScreenFont) {
            return false
        }
        return name == other.name &&
            shadow == other.shadow &&
            bold == other.bold &&
            italic == other.italic
    }

    // 逐位复刻 Objects.hash(name, shadow, bold, italic)：种子 1、31 倍累乘。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + (name?.hashCode() ?: 0)
        result = 31 * result + (shadow?.hashCode() ?: 0)
        result = 31 * result + (bold?.hashCode() ?: 0)
        result = 31 * result + (italic?.hashCode() ?: 0)
        return result
    }

    override fun toString(): String =
        "UiScreenFont{name=" + name + ",shadow=" + shadow +
            ",bold=" + bold + ",italic=" + italic + "}"

    companion object {
        /** 空实例：所有字段为 null（等价于未声明界面字体）。 */
        // @JvmField 使 Java 侧仍以 UiScreenFont.EMPTY 静态字段访问。
        @JvmField
        val EMPTY = UiScreenFont(null, null, null, null)
    }
}
